<template>
  <ul ref="listEl" class="settings-list-group" :class="{mobile: isMobile}" tabindex="-1">
    <template v-for="([heading, buttons], index) in list" :key="index">
      <hr v-if="heading === undefined" />
      <h6 v-else-if="heading" :class="{'fs-6': isMobile}" class="mb-3">{{ translate(heading) }}</h6>
      <BootstrapButton
        v-for="[name, icon, itemKey] in buttons"
        :id="(itemKey ?? name).toLowerCase().replace(/[ :]/g, '-')"
        :key="itemKey ?? name"
        :name="name"
        class="settings-item"
        :disabled="heading === 'Coming Soon'"
        :outlined="isMobile"
        :variant="getVariant(itemKey ?? name)"
        :aria-current="highlight === (itemKey ?? name) || undefined"
        :size="isMobile ? 'lg' : 'sm'"
        @click="() => handleActive(itemKey ?? name)"
      >
        <Icon :icon="`material-symbols:${icon}`" />
        <span>{{ translate(name) }}</span>
      </BootstrapButton>
    </template>
    <slot></slot>
  </ul>
</template>
<script lang="ts" setup>
import {useLocalisation} from '@/composables/useLocalisation';
const props = defineProps<{
  // heading: [button, icon, key]. The key tells buttons that are named alike apart (default: the name)
  list: Map<string | undefined, [string, string, string?][]>;
  highlight?: string; // E.g. the keyboard selection, which doesn't change the model
}>();
const emit = defineEmits<{'update:modelValue': [name: string]}>();
const active = defineModel<string>({required: false});
const {translate} = useLocalisation();
const {isMobile} = useBsBreakpoints();

const listEl = useTemplateRef<HTMLElement>('listEl');
watch(
  () => props.highlight,
  async () => {
    await nextTick();
    listEl.value?.querySelector('[aria-current="true"]')?.scrollIntoView?.({block: 'nearest'});
  },
);

const getVariant = (name: string) => {
  if (props.highlight === name) return 'primary';
  if (isMobile.value) return 'secondary';
  return active.value === name ? 'primary' : 'ghost';
};

const handleActive = (name: string) => {
  if (active.value !== undefined) {
    active.value = name; // Don't set the model if it wasn't initially provided
  } else {
    emit('update:modelValue', name);
  }
};
</script>
<style lang="scss" scoped>
.settings-list-group {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  height: 100%;
  min-width: 12rem;
  overflow-y: auto;
  padding: 0;

  h6:not(:first-child) {
    margin-top: 0.75rem;
  }

  h6 {
    font-size: 0.75rem;
    font-weight: 600;
  }
}

:deep(.btn) {
  text-align: start;
  white-space: nowrap;
  margin-bottom: 0.1rem;

  span,
  svg {
    flex-shrink: 0;
  }

  svg:first-of-type {
    margin-right: 0.5rem;
  }
}

.mobile :deep(.settings-item) {
  margin-bottom: 0;
  border-radius: 0;

  // Last item in each group
  &:not(:has(+ .settings-item)) {
    border-bottom-left-radius: var(--bs-border-radius);
    border-bottom-right-radius: var(--bs-border-radius);
    margin-bottom: 0.75rem;
  }
}

// First item after each heading
.mobile h6 + :deep(.settings-item) {
  border-top-left-radius: var(--bs-border-radius);
  border-top-right-radius: var(--bs-border-radius);
}
</style>
